import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';
import { openPdf } from '../utils/pdf';
import { BeoordelingModal, type BeoordeelActie } from '../components/BeoordelingModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const fmt = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

// Vaste volgorde + kleur per bestemming
const BESTEMMING_ORDER = ['allianz', 'foursure', 'belastingdienst', 'collectief', 'lid'];
const BESTEMMING_TONE: Record<string, string> = {
  allianz: 'border-mfb-blue/30 bg-mfb-blue/5',
  foursure: 'border-mfb-orange/30 bg-mfb-orange/5',
  belastingdienst: 'border-slate-300 bg-slate-50',
  collectief: 'border-emerald-300 bg-emerald-50',
  lid: 'border-amber-300 bg-amber-50',
};

export const AdminFinancieelPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };

  const [colls, setColls] = useState<any[]>([]);
  const [cid, setCid] = useState('');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    fetch(`${API_BASE_URL}/api/settlement/collectivities-list`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then((d) => { setColls(d); if (d[0]) setCid(d[0].id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  const load = useCallback(() => {
    if (!cid) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/settlement/financieel?collectivity_id=${cid}`, { headers })
      .then((r) => (r.ok ? r.json() : null)).then(setData).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, accessToken]);
  useEffect(() => { load(); }, [load]);

  const [bezig, setBezig] = useState(false);

  const exporteer = async () => {
    const r = await fetch(`${API_BASE_URL}/api/settlement/financieel/export?collectivity_id=${cid}`, { headers });
    if (!r.ok) return;
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `verrekeningen_${(data?.collectivity?.name || 'collectief').toLowerCase().replace(/\s+/g, '_')}.csv`;
    a.click();
  };

  const herbereken = async () => {
    if (!cid) return;
    setBezig(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/settlement/run-afrekening?collectivity_id=${cid}`, { method: 'POST', headers });
      if (!r.ok) { const e = await r.json().catch(() => ({})); alert(e.detail || 'Herberekenen mislukt'); return; }
      load();
    } finally { setBezig(false); }
  };

  const compLabels: Record<string, string> = data?.component_labels || {};
  const opbouw = data?.opbouw || {};

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-slate-500 max-w-2xl">
            Overzicht en specificatie van de opbouw van verrekeningen per collectief: <strong>Allianz Trade</strong>, <strong>FourSure</strong>,
            <strong> Belastingdienst</strong> en <strong>Collectief</strong>. De verdeelregels zijn aanpasbaar; een wijziging in de verdeling
            verandert <strong>niets</strong> aan wat het lid betaalt — alleen de interne verdeling.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={cid} onChange={(e) => setCid(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
            {colls.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={herbereken} disabled={bezig}
            className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg disabled:opacity-50">
            {bezig ? 'Bezig…' : '↻ (Her)bereken'}
          </button>
          <button onClick={exporteer} className="px-4 py-2 bg-mfb-blue hover:bg-mfb-blue-dark text-white text-sm font-semibold rounded-lg">
            ⤓ Exporteer (CSV)
          </button>
        </div>
      </div>

      {cid && <ContractjaarSectie cid={cid} token={accessToken!} collName={data?.collectivity?.name || ''} onChanged={load} />}

      {loading || !data ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : (
        <>
          {/* Facturatie naar leden — onveranderlijk */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Facturatie naar leden (onveranderlijk)</p>
              <p className="text-2xl font-bold text-mfb-blue mt-1">{fmt(data.leden_facturatie_totaal)}</p>
            </div>
            <p className="text-xs text-slate-500 max-w-md">
              Dit is het totaal dat aan leden is gefactureerd (premie + servicefee + assurantiebelasting + btw). Het aanpassen van de
              verdeelregels hieronder verandert dit bedrag niet.
            </p>
          </div>

          {/* Losse verkoop (buiten de bundel, direct via iDEAL/creditcard afgerekend) */}
          {data.losse_verkoop && data.losse_verkoop.totaal > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Losse verkoop (buiten de bundel, direct betaald)</p>
                <p className="text-2xl font-bold text-mfb-blue mt-1">{fmt(data.losse_verkoop.totaal)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {data.losse_verkoop.aantal} bestelling(en) via iDEAL/creditcard — staat los van de bundel-verrekening hieronder.
                </p>
              </div>
              <div className="text-sm text-slate-600 space-y-0.5 min-w-[180px]">
                {Object.entries(data.losse_verkoop.per_soort || {}).map(([k, v]: any) => (
                  <div key={k} className="flex justify-between gap-6">
                    <span className="text-slate-500 capitalize">{k}</span><span className="tabular-nums">{fmt(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Opbouw per bestemming */}
          <h2 className="font-display text-lg font-semibold text-mfb-blue mb-3">Opbouw verrekeningen</h2>
          {data.aantal_runs === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500 mb-8">
              Nog geen afrekening gedraaid voor dit collectief. Klik rechtsboven op “↻ (Her)bereken”.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {BESTEMMING_ORDER.filter((b) => opbouw[b]).map((b) => {
                const blk = opbouw[b];
                return (
                  <div key={b} className={`rounded-2xl border p-5 ${BESTEMMING_TONE[b] || 'border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-800">{blk.label}</h3>
                      <span className="font-bold text-slate-900 tabular-nums">{fmt(blk.totaal)}</span>
                    </div>
                    <dl className="space-y-1 text-sm">
                      {Object.entries(blk.componenten).map(([comp, bedrag]: any) => (
                        <div key={comp} className="flex justify-between">
                          <dt className="text-slate-500">{compLabels[comp] || comp}</dt>
                          <dd className="text-slate-700 tabular-nums">{fmt(bedrag)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                );
              })}
            </div>
          )}

          {/* Documentstromen */}
          {data.aantal_runs > 0 && <DocumentstromenSection cid={cid} headers={headers} />}

          {/* Verdeelsleutel per opbrengststroom × herkomst */}
          <VerdeelsleutelSection cid={cid} headers={headers} />

          {/* Naverrekening assurantiebelasting per lid (polisjaar) */}
          <NaverrekeningSection cid={cid} headers={headers} />
        </>
      )}
    </Layout>
  );
};

const NaverrekeningSection: React.FC<{ cid: string; headers: any }> = ({ cid, headers }) => {
  const [navs, setNavs] = useState<any[]>([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settlement/naverrekeningen?collectivity_id=${cid}`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then(setNavs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  return (
    <div className="mt-8">
      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-1">Naverrekening assurantiebelasting</h2>
      <p className="text-sm text-slate-500 mb-3">
        Per polisjaar wordt de assurantiebelasting definitief gemaakt: provisioneel over de volledige premie geheven, definitief alleen over het
        NL-verzekerde deel. Het verschil is de <strong>teruggaaf</strong> aan het lid (basis voor de eindejaarsafrekening).
      </p>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {navs.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Nog geen naverrekeningen voor dit collectief.</div>
        ) : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Verzekerd NL', 'Verzekerd export', 'NL-aandeel', 'IPT provisioneel', 'IPT definitief', 'Teruggaaf'].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {navs.map((n) => (
                <tr key={n.id} className="hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-700 tabular-nums">{fmt(n.verzekerd_nl)}</td>
                  <td className="px-5 py-3 text-slate-700 tabular-nums">{fmt(n.verzekerd_export)}</td>
                  <td className="px-5 py-3 text-slate-700 tabular-nums">{(n.nl_aandeel * 100).toFixed(2)}%</td>
                  <td className="px-5 py-3 text-slate-600 tabular-nums">{fmt(n.ipt_provisioneel)}</td>
                  <td className="px-5 py-3 text-slate-600 tabular-nums">{fmt(n.ipt_definitief)}</td>
                  <td className="px-5 py-3 font-semibold text-mfb-orange tabular-nums">{fmt(n.teruggaaf)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
};

const DocumentstromenSection: React.FC<{ cid: string; headers: any }> = ({ cid, headers }) => {
  const [docs, setDocs] = useState<any[]>([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settlement/financieel/documenten?collectivity_id=${cid}`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then(setDocs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  const open = async (soort: string) => {
    const r = await fetch(`${API_BASE_URL}/api/settlement/financieel/document/${soort}?collectivity_id=${cid}`, { headers });
    if (!r.ok) return;
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), '_blank');
  };

  return (
    <div className="mb-8">
      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-1">Documentstromen</h2>
      <p className="text-sm text-slate-500 mb-3">
        Documenten uit de geboekte afrekening: creditnota commissie (premie btw-vrijgesteld, servicefee 21% btw),
        afrekening Allianz, eigen opbrengst FourSure en het belastingoverzicht. Open als PDF.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {docs.map((d) => (
          <button key={d.soort} onClick={() => d.beschikbaar && open(d.soort)} disabled={!d.beschikbaar}
            className={`text-left rounded-2xl border p-4 transition ${d.beschikbaar ? 'border-slate-200 bg-white hover:border-mfb-orange hover:shadow' : 'border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed'}`}>
            <div className="text-sm font-semibold text-mfb-blue">{d.titel}</div>
            <div className="mt-2 text-lg font-bold text-slate-900 tabular-nums">{fmt(d.totaal)}</div>
            <div className="mt-1 text-xs font-medium text-mfb-orange">{d.beschikbaar ? '📄 Open PDF →' : 'Geen data'}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

const STROOM_LABEL: Record<string, string> = {
  premie: 'Premie', kredietopinies: 'Servicefee kredietopinies', incasso: 'Servicefee incasso',
};
const STROOM_BTW: Record<string, string> = {
  premie: 'commissie btw-vrijgesteld', kredietopinies: 'commissie 21% btw', incasso: 'commissie 21% btw',
};

const VerdeelsleutelSection: React.FC<{ cid: string; headers: any }> = ({ cid, headers }) => {
  const [huidig, setHuidig] = useState<any[]>([]);
  const [msg, setMsg] = useState('');

  const laad = useCallback(async () => {
    const r = await fetch(`${API_BASE_URL}/api/settlement/verdeelsleutel?collectivity_id=${cid}`, { headers });
    if (r.ok) { const d = await r.json(); setHuidig(d.huidig || []); }
  }, [cid, headers]);
  useEffect(() => { laad(); }, [laad]);

  const flash = (t: string) => { setMsg(t); setTimeout(() => setMsg(''), 3000); };

  return (
    <div>
      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-1">Verdeelsleutel per opbrengststroom</h2>
      <p className="text-sm text-slate-500 mb-3">
        Per stroom en per herkomst (binnen pakket = bundel, buiten pakket = los) verdeel je de opbrengst over Allianz, het collectief en FourSure.
        De som moet 100% zijn — dit verandert niets aan wat het lid betaalt, alleen de doorbelasting. Een wijziging legt een nieuwe versie vast (correctie).
        Belasting wordt bruto afgedragen (assurantiebelasting over premie, 21% btw over de servicestromen).
      </p>
      {msg && <div className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{msg}</div>}
      <div className="space-y-5">
        {['premie', 'kredietopinies', 'incasso'].map((stroom) => (
          <div key={stroom} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-mfb-blue">{STROOM_LABEL[stroom]}</h3>
              <span className="text-xs text-slate-400">{STROOM_BTW[stroom]}</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {['bundel', 'los'].map((herkomst) => (
                <VerdelingRij key={herkomst} cid={cid} headers={headers} stroom={stroom} herkomst={herkomst}
                  rij={huidig.find((h) => h.stroom === stroom && h.herkomst === herkomst)}
                  onSaved={() => { laad(); flash(`${STROOM_LABEL[stroom]} (${herkomst}) opgeslagen`); }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const VerdelingRij: React.FC<{ cid: string; headers: any; stroom: string; herkomst: string; rij: any; onSaved: () => void }> = ({ cid, headers, stroom, herkomst, rij, onSaved }) => {
  const [allianz, setAllianz] = useState('');
  const [collectief, setCollectief] = useState('');
  const [foursure, setFoursure] = useState('');
  const [vanaf, setVanaf] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    setAllianz(rij?.allianz_pct != null ? String(rij.allianz_pct) : '');
    setCollectief(rij?.collectief_pct != null ? String(rij.collectief_pct) : '');
    setFoursure(rij?.foursure_pct != null ? String(rij.foursure_pct) : '');
  }, [rij]);

  const som = Math.round(((+allianz || 0) + (+collectief || 0) + (+foursure || 0)) * 100) / 100;
  const inp = 'w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm';

  const save = async () => {
    setErr('');
    if (som !== 100) { setErr(`Som moet 100% zijn (nu ${som}%)`); return; }
    if (!vanaf) { setErr('Kies een ingangsdatum'); return; }
    const r = await fetch(`${API_BASE_URL}/api/settlement/verdeelsleutel`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ stroom, herkomst, allianz_pct: +allianz, collectief_pct: +collectief, foursure_pct: +foursure, geldig_vanaf: vanaf, collectivity_id: cid }),
    });
    if (r.ok) { setVanaf(''); onSaved(); }
    else { const e = await r.json().catch(() => ({})); setErr(e.detail || 'Opslaan mislukt'); }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${herkomst === 'bundel' ? 'bg-mfb-blue/10 text-mfb-blue' : 'bg-mfb-orange/10 text-mfb-orange'}`}>
          {herkomst === 'bundel' ? 'Binnen pakket (bundel)' : 'Buiten pakket (los)'}
        </span>
        <span className="text-[11px] text-slate-400">{rij?.ingesteld ? `vanaf ${rij.geldig_vanaf}` : 'nog niet ingesteld'}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div><label className="block text-[11px] text-slate-500 mb-0.5">Allianz %</label><input type="number" step="0.01" value={allianz} onChange={(e) => setAllianz(e.target.value)} className={inp} /></div>
        <div><label className="block text-[11px] text-slate-500 mb-0.5">Collectief %</label><input type="number" step="0.01" value={collectief} onChange={(e) => setCollectief(e.target.value)} className={inp} /></div>
        <div><label className="block text-[11px] text-slate-500 mb-0.5">FourSure %</label><input type="number" step="0.01" value={foursure} onChange={(e) => setFoursure(e.target.value)} className={inp} /></div>
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1"><label className="block text-[11px] text-slate-500 mb-0.5">Ingangsdatum</label><input type="date" value={vanaf} onChange={(e) => setVanaf(e.target.value)} className={inp} /></div>
        <span className={`text-xs font-medium ${som === 100 ? 'text-emerald-600' : 'text-mfb-orange'}`}>{som}%</span>
        <button onClick={save} className="px-3 py-1.5 bg-mfb-orange hover:bg-mfb-orange-dark text-white text-sm font-semibold rounded-lg">Opslaan</button>
      </div>
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
    </div>
  );
};

const PARTIJ_LABEL: Record<string, string> = {
  collectief: 'Collectief', allianz: 'Allianz Trade', foursure: 'FourSure', belastingdienst: 'Belastingdienst',
};
const PARTIJ_TEMPLATE: Record<string, string> = {
  collectief: 'afrekening_collectief', allianz: 'afrekening_allianz',
  foursure: 'afrekening_foursure', belastingdienst: 'afrekening_belastingdienst',
};

const ContractjaarSectie: React.FC<{ cid: string; token: string; collName: string; onChanged: () => void }> = ({ cid, token, collName, onChanged }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const notify = useToast();
  const [periodes, setPeriodes] = useState<any[]>([]);
  const [pid, setPid] = useState('');
  const [detail, setDetail] = useState<any>(null);
  const [bezig, setBezig] = useState(false);
  const [versturen, setVersturen] = useState<{ partij: string; bedrag: number } | null>(null);

  const laadPeriodes = useCallback(() => {
    fetch(`${API_BASE_URL}/api/settlement/afrekenperiodes?collectivity_id=${cid}`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then((d) => { setPeriodes(d); setPid((p) => p || (d[0]?.id ?? '')); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, token]);
  useEffect(() => { setPid(''); laadPeriodes(); }, [laadPeriodes]);

  const laadDetail = useCallback(() => {
    if (!pid) { setDetail(null); return; }
    fetch(`${API_BASE_URL}/api/settlement/afrekenperiode/${pid}`, { headers })
      .then((r) => (r.ok ? r.json() : null)).then(setDetail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, token]);
  useEffect(() => { laadDetail(); }, [laadDetail]);

  const afsluiten = async () => {
    if (!detail || detail.status !== 'open') return;
    if (!window.confirm(`Contractjaar ${detail.jaar} afsluiten? De cijfers worden vergrendeld, de afrekeningen per partij worden vastgezet en het volgende jaar wordt geopend.`)) return;
    setBezig(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/settlement/afrekenperiode/${pid}/afsluiten`, { method: 'POST', headers });
      if (!r.ok) { const e = await r.json().catch(() => ({})); notify.error(e.detail || 'Afsluiten mislukt'); return; }
      const d = await r.json();
      notify.success(`Jaar ${d.afgesloten_jaar} afgesloten — ${d.partij_afrekeningen} afrekeningen. Jaar ${d.nieuw_open_jaar} geopend.`);
      laadPeriodes(); laadDetail(); onChanged();
    } finally { setBezig(false); }
  };

  const verstuurActies = (partij: string, bedrag: number): BeoordeelActie[] => [{
    key: 'versturen', label: 'Versturen', kleur: 'green',
    template_key: PARTIJ_TEMPLATE[partij], bijlage: `${PARTIJ_LABEL[partij]}-document (PDF)`,
    context: (_r, _t) => ({ collectief: collName, jaar: detail?.jaar, bedrag: fmt(bedrag) }),
  }];
  const voerVerstuurUit = async (partij: string, onderwerp: string, bericht: string) => {
    const r = await fetch(`${API_BASE_URL}/api/settlement/afrekenperiode/${pid}/partij/${partij}/versturen`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ onderwerp: onderwerp || null, bericht: bericht || null }),
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); notify.error(e.detail || 'Versturen mislukt'); return null; }
    const d = await r.json();
    notify.success(d.email_verzonden ? `Afrekening verstuurd naar ${d.ontvanger}.` : `Afrekening vastgelegd (e-mail niet verzonden — SMTP niet ingesteld).`);
    laadDetail();
    return d;
  };

  const open = detail && detail.status === 'open';

  return (
    <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-mfb-blue">Contractjaar-afwikkeling</h2>
          <p className="text-sm text-slate-500">Sluit een contractjaar af en verstuur de afrekening per partij. Afgesloten jaren zijn vergrendeld.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={pid} onChange={(e) => setPid(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
            {periodes.map((p) => <option key={p.id} value={p.id}>{p.jaar} — {p.status}</option>)}
          </select>
          {open && (
            <button onClick={afsluiten} disabled={bezig}
              className="px-4 py-2 bg-mfb-orange hover:bg-orange-600 text-white text-sm font-semibold rounded-lg disabled:opacity-50">
              {bezig ? 'Bezig…' : `Jaar ${detail?.jaar} afsluiten`}
            </button>
          )}
        </div>
      </div>

      {!detail ? (
        <div className="text-sm text-slate-400">Geen periode geselecteerd.</div>
      ) : (
        <>
          {open && <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">Dit jaar is nog open — bedragen zijn voorlopig. Versturen kan pas na afsluiten.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {detail.partijen.map((p: any) => (
              <div key={p.partij} className="rounded-xl border border-slate-200 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{PARTIJ_LABEL[p.partij]}</div>
                <div className="text-xl font-display font-bold tabular-nums text-slate-800 mt-1">{fmt(p.bedrag)}</div>
                {p.btw > 0 && <div className="text-[11px] text-slate-400">incl. btw {fmt(p.btw)}</div>}
                <div className="mt-2">
                  {p.status === 'verstuurd'
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">verstuurd {p.verstuurd_op}</span>
                    : p.status === 'definitief'
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-mfb-blue/10 text-mfb-blue font-medium">definitief</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">voorlopig</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => openPdf(`/api/settlement/afrekenperiode/${pid}/partij/${p.partij}/document`, token)}
                    className="text-xs font-medium text-mfb-blue hover:underline">📄 Document</button>
                  {!open && (
                    <button onClick={() => setVersturen({ partij: p.partij, bedrag: p.bedrag })}
                      className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-2.5 py-1">
                      {p.status === 'verstuurd' ? 'Opnieuw versturen' : 'Versturen'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {versturen && (
        <BeoordelingModal
          titel={`Afrekening versturen — ${PARTIJ_LABEL[versturen.partij]}`}
          context={`${collName} · jaar ${detail?.jaar} · ${fmt(versturen.bedrag)}`}
          acties={verstuurActies(versturen.partij, versturen.bedrag)} token={token}
          uitvoeren={(_a, _r, _t, ond, ber) => voerVerstuurUit(versturen.partij, ond, ber)}
          onClose={() => setVersturen(null)} onDone={() => setVersturen(null)}
        />
      )}
    </div>
  );
};

export default AdminFinancieelPage;
